import { carriersGetCarriersOptions, useHeyApi } from "@platform/api";
import { useRfpCarriers } from "@platform/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  buildCarrierNameById,
  buildCarrierNameByIdFromDirectory,
  resolveProposalCarrierNames,
} from "../utils/quotesCarrierNames";

// Resolves carrier display names (carrierId → name) and remaps proposals so the
// grid shows real carrier names, not slugs. The name comes from two sources merged
// together: the full centro-portal carrier directory (whose `id` equals the BTX
// proposal carrierId, so it covers every real carrier including ones not invited
// to this RFP) and the RFP-scoped carriers (which additionally cover the internal
// autoquote pseudo-carriers the directory omits). RFP-scoped names take precedence.
// Extracted into its own hook to keep useQuotesData under the complexity limit.
export const useProposalsWithCarrierNames = <
  T extends { carrierId: string; carrierName: string },
>(
  rfpId: string | undefined,
  proposals: T[],
): T[] => {
  const { centroPortalClient } = useHeyApi();

  const { data: directoryNameById } = useQuery({
    ...carriersGetCarriersOptions({ client: centroPortalClient }),
    staleTime: 5 * 60 * 1000,
    select: buildCarrierNameByIdFromDirectory,
  });

  const { data: rfpNameById } = useRfpCarriers<Map<string, string>>({
    rfpId: rfpId ?? "",
    enabled: !!rfpId,
    select: buildCarrierNameById,
  });

  const carrierNameById = useMemo(() => {
    const merged = new Map(directoryNameById);
    rfpNameById?.forEach((name, id) => merged.set(id, name));
    return merged;
  }, [directoryNameById, rfpNameById]);

  return useMemo(
    () => resolveProposalCarrierNames(proposals, carrierNameById),
    [proposals, carrierNameById],
  );
};
